import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import UpgradeModal from "@/components/UpgradeModal";
import TrialBanner from "@/components/TrialBanner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import MobileBottomNav from "@/layout/MobileBottomNav";
import MobileTopBar from "@/layout/MobileTopBar";
import DesktopSidebar from "@/layout/DesktopSidebar";
import api from "@/lib/api";
import { subscribeUpgradePrompt } from "@/lib/upgradePrompt";

// See @/config/nav.config.js for the shared nav list used by desktop
// sidebar + mobile bottom-bar + More sheet. Adding a route there
// wires it into every surface at once.

export default function AppLayout({ children }) {
  const { user, logout, trialStatus } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [upgradeDetail, setUpgradeDetail] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);

  // Load subscription status once so the sidebar can render an Upgrade
  // pill for free users, or a "Pro" chip for paid ones. Silent on failure.
  useEffect(() => {
    let cancelled = false;
    api.get("/billing/status").then(({ data }) => {
      if (!cancelled) setBillingStatus(data);
    }).catch(() => { /* silent */ });
    const unsub = subscribeUpgradePrompt((d) => setUpgradeDetail(d));
    return () => { cancelled = true; unsub(); };
  }, []);

  // Sync the global write-lock flag on <html> so `index.css` can visually
  // disable every add/edit/delete button in one place. Cleanup on unmount
  // clears the flag so /pricing, /billing/success, and public pages
  // (rendered without AppLayout) never see it.
  useEffect(() => {
    const el = document.documentElement;
    if (trialStatus?.is_locked) {
      el.setAttribute("data-write-locked", "true");
    } else {
      el.removeAttribute("data-write-locked");
    }
    return () => el.removeAttribute("data-write-locked");
  }, [trialStatus?.is_locked]);

  const isPro = !!billingStatus?.is_pro;
  // Admins act as "always Pro" for gating purposes — they see every
  // feature unlocked (no lock icons, no Pricing bounce) so they can
  // demo, debug, and support the app end-to-end. `hasFullAccess` is
  // the single boolean the sidebar + upgrade prompts consult.
  const isAdmin = !!user?.is_admin;
  const hasFullAccess = isPro || isAdmin;

  // "Pro expires in X days" — shown when the current Pro period has 7
  // days or less remaining AND auto-renew is off (which is the current
  // default). Nudge the user to renew before they lapse to free.
  const daysUntilExpiry = (() => {
    const cpe = billingStatus?.current_period_end;
    if (!isPro || !billingStatus?.cancel_at_period_end || !cpe) return null;
    try {
      const end = new Date(cpe).getTime();
      const now = Date.now();
      const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return days > 0 && days <= 7 ? days : null;
    } catch { return null; }
  })();

  const handleProLocked = (feature) => setUpgradeDetail({
    feature,
    message: t("upgrade.pro_feature_locked", { defaultValue: "This is a Pro feature." }),
  });

  return (
    <div className="min-h-screen flex bg-[var(--bg-primary)] flex-col lg:flex-row">
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0">
        <DesktopSidebar
          user={user}
          isAdmin={isAdmin}
          isPro={isPro}
          hasFullAccess={hasFullAccess}
          billingStatus={billingStatus}
          trialStatus={trialStatus}
          daysUntilExpiry={daysUntilExpiry}
          theme={theme}
          onToggleTheme={toggleTheme}
          onLogout={logout}
          onProLocked={handleProLocked}
        />

        <MobileTopBar
          user={user}
          isAdmin={isAdmin}
          theme={theme}
          onToggleTheme={toggleTheme}
          onLogout={logout}
        />

        <main className="flex-1 min-w-0 pt-14 lg:pt-0 pb-24 lg:pb-0">
          <TrialBanner />
          <div className="max-w-6xl mx-auto px-5 lg:px-10 py-8 lg:py-10">
            {children}
          </div>
        </main>

        {/* Native-app-style bottom bar. Only visible on <lg viewports. */}
        <MobileBottomNav
          hasFullAccess={hasFullAccess}
          isAdmin={isAdmin}
          onProLocked={handleProLocked}
        />

        <UpgradeModal
          open={!!upgradeDetail}
          detail={upgradeDetail}
          onClose={() => setUpgradeDetail(null)}
        />
      </div>
    </div>
  );
}
